// Shared by the admin's shareable stats card and the player-facing daily
// stats screen, so both render the exact same breakdown from the same logic.
export function computeDailyStats(gameDocs) {
  let cc = 0, dd = 0, cd = 0;
  let questionText = null;
  let questionChoices = null;
  let agreedTotal = 0, agreedTrust = 0, disagreedTotal = 0, disagreedTrust = 0;
  const pairMap = {};

  gameDocs.forEach((data) => {
    const isCC = data.playerAMove === "cooperate" && data.playerBMove === "cooperate";
    const isDD = data.playerAMove === "defect" && data.playerBMove === "defect";
    if (isCC) cc++;
    else if (isDD) dd++;
    else cd++;

    // Same question/answer-pair breakdown for every game that day, since
    // dailyConfig pins one question (or none) to the whole day.
    if (data.question && data.answerA && data.answerB) {
      questionText = data.question.text;
      questionChoices = data.question.choices || questionChoices;
      const agree = data.answerA === data.answerB;

      if (agree) {
        agreedTotal++;
        if (isCC) agreedTrust++;
      } else {
        disagreedTotal++;
        if (isCC) disagreedTrust++;
      }

      const pair = [data.answerA, data.answerB].sort();
      const key = pair.join(" & ");
      if (!pairMap[key]) {
        pairMap[key] = {
          key, choices: pair, agree, total: 0, cc: 0, dd: 0, cd: 0,
          // Directional 2x2 (which CHOICE's picker trusted/betrayed), only
          // meaningful when the pair disagreed — cc/dd/cd above collapse
          // "A trusted, B betrayed" and "A betrayed, B trusted" into one
          // bucket, which is fine when both picked the same answer (doesn't
          // matter who's who) but loses exactly the info this matrix is for
          // when they picked differently.
          matrix: { coopCoop: 0, coopDefect: 0, defectCoop: 0, defectDefect: 0 },
        };
      }
      pairMap[key].total++;
      if (isCC) pairMap[key].cc++;
      else if (isDD) pairMap[key].dd++;
      else pairMap[key].cd++;

      if (!agree) {
        const [choice0] = pair;
        const aIsChoice0 = data.answerA === choice0;
        const move0 = aIsChoice0 ? data.playerAMove : data.playerBMove;
        const move1 = aIsChoice0 ? data.playerBMove : data.playerAMove;
        if (move0 === "cooperate" && move1 === "cooperate") pairMap[key].matrix.coopCoop++;
        else if (move0 === "cooperate" && move1 === "defect") pairMap[key].matrix.coopDefect++;
        else if (move0 === "defect" && move1 === "cooperate") pairMap[key].matrix.defectCoop++;
        else pairMap[key].matrix.defectDefect++;
      }
    }
  });

  // Show every possible answer-pair permutation (A v A, A v B, B v B, ...),
  // not just the ones that actually happened, so a 0-game combination is
  // visible as a gap rather than silently missing from the breakdown.
  let pairs = [];
  if (questionChoices && questionChoices.length) {
    for (let i = 0; i < questionChoices.length; i++) {
      for (let j = i; j < questionChoices.length; j++) {
        const pair = [questionChoices[i], questionChoices[j]].sort();
        const key = pair.join(" & ");
        pairs.push(pairMap[key] || { key, choices: pair, agree: pair[0] === pair[1], total: 0, cc: 0, dd: 0, cd: 0, matrix: { coopCoop: 0, coopDefect: 0, defectCoop: 0, defectDefect: 0 } });
      }
    }
  } else {
    pairs = Object.values(pairMap);
  }
  pairs.sort((a, b) => b.total - a.total);

  const answerBreakdown = questionText ? {
    questionText,
    agreedTotal, agreedTrust,
    disagreedTotal, disagreedTrust,
    pairs,
  } : null;

  return { totalGames: gameDocs.length, cc, dd, cd, answerBreakdown };
}
